import { commodityStrings } from '@data/commodities';
import { FileTypeHelper } from '@models/next/FileType';
import { LocalFile, RemoteFile } from '@models/next/FileType';
import { PaginationType } from '@models/next/PaginationType';
import { z } from 'zod';

export const CommodityActionStatus = z.enum(['edit', 'createSuccess', 'editSuccess']);

export type CommodityActionStatus = z.infer<typeof CommodityActionStatus>;

export const CommodityFields = z.object({
  id: z.number(),
  commodity_no: z.string().label(commodityStrings.commodity_no),
  name_en: z.string().label(commodityStrings.name_en),
  name_ja: z.string().label(commodityStrings.name_ja),
  url: z.string().label(commodityStrings.url),
  material: z.string().label(commodityStrings.material),
  usage: z.string().label(commodityStrings.usage),
  explanation: z.string().label(commodityStrings.explanation),
  memo: z.string().label(commodityStrings.memo),
  images: z.array(RemoteFile).label(commodityStrings.images),
  local_images: z.array(LocalFile).label(commodityStrings.images),
  hs_code: z.string().label(commodityStrings.hs_code),
});

export const Commodity = z.object({
  id: CommodityFields.shape.id,
  commodity_no: CommodityFields.shape.commodity_no,
  name_en: CommodityFields.shape.name_en,
  name_ja: CommodityFields.shape.name_ja,
  url: CommodityFields.shape.url,
  material: CommodityFields.shape.material,
  usage: CommodityFields.shape.usage,
  explanation: CommodityFields.shape.explanation,
  memo: CommodityFields.shape.memo,
  images: CommodityFields.shape.images,
  hs_code: CommodityFields.shape.hs_code,
});

export type Commodity = z.infer<typeof Commodity>;

export const PostCommodity = z.object({
  commodity_no: CommodityFields.shape.commodity_no,
  name_en: CommodityFields.shape.name_en,
  name_ja: CommodityFields.shape.name_ja,
  url: CommodityFields.shape.url,
  material: CommodityFields.shape.material,
  usage: CommodityFields.shape.usage,
  explanation: CommodityFields.shape.explanation,
  memo: CommodityFields.shape.memo,
  images: CommodityFields.shape.images,
});

export type PostCommodity = z.infer<typeof PostCommodity>;

export const PatchCommodity = PostCommodity;

export type PatchCommodity = z.infer<typeof PatchCommodity>;

export const CommoditySearchQuery = z
  .object({
    user_id: z.number(),
    commodity_no: CommodityFields.shape.commodity_no,
    name_en: CommodityFields.shape.name_en,
    ordering: z.array(z.string()),
    page: PaginationType.shape.page,
    page_size: PaginationType.shape.page_size,
  })
  .partial();

export type CommoditySearchQuery = z.infer<typeof CommoditySearchQuery>;

export const CommodityPages = z.object({
  count: z.number(),
  results: Commodity.array(),
});

export type CommodityPages = z.infer<typeof CommodityPages>;

export const CommodityForm = z.object({
  commodity_no: CommodityFields.shape.commodity_no.min(1),
  name_en: CommodityFields.shape.name_en.min(1),
  name_ja: CommodityFields.shape.name_ja,
  url: CommodityFields.shape.url,
  material: CommodityFields.shape.material.min(1),
  usage: CommodityFields.shape.usage.min(1),
  explanation: CommodityFields.shape.explanation,
  memo: CommodityFields.shape.memo,
  local_images: CommodityFields.shape.local_images.max(6, commodityStrings.maxImages),
  hs_code: CommodityFields.shape.hs_code,
});

export type CommodityForm = z.infer<typeof CommodityForm>;

export const AdminCommodityForm = z.object({
  hs_code: CommodityFields.shape.hs_code.min(1),
});

export type AdminCommodityForm = z.infer<typeof AdminCommodityForm>;

export class CommodityHelpers {
  static mapToForm(commodity: Commodity): CommodityForm {
    return {
      ...commodity,
      local_images: commodity.images.map(FileTypeHelper.remoteToLocal),
    };
  }

  static mapToAdminForm(commodity: Commodity): AdminCommodityForm {
    return {
      ...commodity,
    };
  }
}
